import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CalendarPlus, AlertCircle, Edit, Trash2, DollarSign, Search } from "lucide-react";
import { format } from "date-fns";
import { SubscriptionType } from "@/lib/supabase/types";
import EditSubscriptionDialog from "./dialogs/EditSubscriptionDialog";
import UpdateSubscriptionPaymentDialog from "./dialogs/UpdateSubscriptionPaymentDialog";
import { Input } from "@/components/ui/input";

interface SubscriptionsTabProps {
  subscriptions: SubscriptionType[] | undefined;
  isLoading: boolean;
  error: unknown;
  onAddSubscription: () => void;
  onUpdateStatus: (subscriptionId: string, active: boolean) => void;
  onUpdatePaymentStatus: (subscriptionId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => void;
  onEditSubscription: (subscriptionId: string, updates: {
    totalClasses: number;
    remainingClasses: number;
    expirationDate: string;
  }) => void;
  onDeleteSubscription: (subscriptionId: string) => void;
  filteredSubscriptions?: SubscriptionType[];
  subscriptionFilter?: string;
  setSubscriptionFilter?: (filter: string) => void;
}

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ 
  subscriptions, 
  isLoading, 
  error, 
  onAddSubscription,
  onUpdateStatus,
  onUpdatePaymentStatus,
  onEditSubscription,
  onDeleteSubscription,
  filteredSubscriptions,
  subscriptionFilter = "",
  setSubscriptionFilter = () => {}
}) => {
  const [subscriptionToUpdate, setSubscriptionToUpdate] = useState<{ id: string; active: boolean } | null>(null);
  const [subscriptionToUpdatePayment, setSubscriptionToUpdatePayment] = useState<SubscriptionType | null>(null);
  const [subscriptionToEdit, setSubscriptionToEdit] = useState<SubscriptionType | null>(null);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<SubscriptionType | null>(null);
  
  const displaySubscriptions = filteredSubscriptions || subscriptions;

  const handleEditSubscription = (subscription: SubscriptionType) => {
    setSubscriptionToEdit(subscription);
  };
  
  const handleDeleteSubscription = (subscription: SubscriptionType) => {
    setSubscriptionToDelete(subscription);
  };

  const handleUpdatePaymentStatus = (subscription: SubscriptionType) => {
    setSubscriptionToUpdatePayment(subscription);
  };

  const getPaymentStatusBadge = (paymentStatus?: string) => {
    switch (paymentStatus) {
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <DollarSign className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'nptr':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">
            <AlertCircle className="h-3 w-3 mr-1" />
            NPTR
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">All Subscriptions</h2>
        <Button onClick={onAddSubscription}>
          <CalendarPlus className="mr-2 h-4 w-4" />
          Create Subscription
        </Button>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by name or email"
          className="pl-8 w-full"
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
        />
      </div>
      
      <Card>
        <CardContent className="pt-6">
          {error ? (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error Loading Subscriptions</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : "Failed to load subscriptions."}
              </AlertDescription>
            </Alert>
          ) : isLoading ? (
            <div className="text-center py-6">Loading subscriptions...</div>
          ) : displaySubscriptions && displaySubscriptions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead>Expiration Date</TableHead>
                  <TableHead>Classes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displaySubscriptions.map((subscription) => {
                  const isPast = new Date(subscription.expirationDate) < new Date();
                  const isLowRemaining = subscription.remainingClasses <= 2 && subscription.remainingClasses > 0;
                  const isRemaining = subscription.remainingClasses > 0;
                  
                  return (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>
                              {subscription.userName ? 
                                `${subscription.userName.split(' ')[0][0]}${subscription.userName.split(' ')[1]?.[0] || ''}` : 
                                'UN'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium">{subscription.userName || 'Unknown User'}</span>
                            <span className="text-xs text-muted-foreground">{subscription.userEmail || ''}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{format(new Date(subscription.purchaseDate), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {format(new Date(subscription.expirationDate), 'MMM d, yyyy')}
                          {isPast && (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              Expired
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{subscription.remainingClasses} of {subscription.totalClasses}</span>
                          {!isRemaining && (
                            <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">
                              Used
                            </Badge>
                          )}
                          {isLowRemaining && (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              Low
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {subscription.active ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusBadge(subscription.payment_status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdatePaymentStatus(subscription)}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            Payment
                          </Button>
                          <Button
                            variant={subscription.active ? "outline" : "default"}
                            size="sm"
                            onClick={() => setSubscriptionToUpdate({
                              id: subscription.id,
                              active: !subscription.active
                            })}
                          >
                            {subscription.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditSubscription(subscription)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleDeleteSubscription(subscription)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              {subscriptionFilter ? "No matching subscriptions found." : "No subscriptions found."}
            </div>
          )}
        </CardContent>
      </Card>
      
      <AlertDialog 
        open={!!subscriptionToUpdate} 
        onOpenChange={(open) => !open && setSubscriptionToUpdate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {subscriptionToUpdate?.active ? "Activate" : "Deactivate"} Subscription
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {subscriptionToUpdate?.active ? "activate" : "deactivate"} this subscription?
              {!subscriptionToUpdate?.active && " This will prevent the user from using this subscription for future bookings."}
              {subscriptionToUpdate?.active && " This will allow the user to use this subscription for future bookings."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (subscriptionToUpdate) {
                  onUpdateStatus(subscriptionToUpdate.id, subscriptionToUpdate.active);
                  setSubscriptionToUpdate(null);
                }
              }}
            >
              {subscriptionToUpdate?.active ? "Activate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <UpdateSubscriptionPaymentDialog
        isOpen={!!subscriptionToUpdatePayment}
        onOpenChange={(open) => !open && setSubscriptionToUpdatePayment(null)}
        subscription={subscriptionToUpdatePayment}
        onUpdatePaymentStatus={(id, status) => {
          onUpdatePaymentStatus(id, status);
          setSubscriptionToUpdatePayment(null);
        }}
      />
      
      <EditSubscriptionDialog 
        isOpen={!!subscriptionToEdit}
        onOpenChange={(open) => !open && setSubscriptionToEdit(null)}
        subscription={subscriptionToEdit}
        onSave={(id, updates) => {
          onEditSubscription(id, updates);
          setSubscriptionToEdit(null);
        }}
      />
      
      <AlertDialog 
        open={!!subscriptionToDelete} 
        onOpenChange={(open) => !open && setSubscriptionToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this subscription for {subscriptionToDelete?.userName || 'this user'}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (subscriptionToDelete) {
                  onDeleteSubscription(subscriptionToDelete.id);
                  setSubscriptionToDelete(null);
                }
              }}
            >
              Delete Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionsTab;
